import { useMemo, useRef, useState, useEffect } from 'react';
import gettext from '@sdk/lang/gettext';
import formatDate from '@sdk/utils/dateFormat/formatDate';
import getNowDate from '@sdk/utils/getNowDate';
import { ClientFlags, OnboardingTrialStepsType } from '@sdk/authentication/types';
import type { OrgSettings } from '@sdk/authentication/types/OrganisationType';
import type { PersonasDict } from '@sdk/personaCollection/types/PersonasDict';
import { LocationType } from '@sdk/locations/types/LocationType';

import type {
  RestangularEmployee,
  RestangularPosition,
} from '@web/onboarding/Checklist/DefineWagesModal/defineWagesModal.types';
import type {
  NavigateToSchedule,
  OpenCreateShiftModal,
  OnboardingStepsConfigType,
  NavigateToReports,
  Event,
  ShowPublishShiftModal,
  ShowPublishShiftTooltip,
} from '@web/onboarding/Checklist/onboardingChecklist.types';
import useStepsConfig from '@web/onboarding/Checklist/hooks/useStepsConfig';
import usePublishSchedule from '@web/onboarding/Checklist/hooks/usePublishSchedule';
import useAutocompleteSteps from '@web/onboarding/Checklist/hooks/useAutocompleteSteps';
import { useCompleteStep } from '@web/onboarding/Checklist/hooks/useCompleteStep';
import { getSupportedSteps } from '@web/onboarding/Checklist/onboardingChecklist.utils';
import { getMappedLocations } from '@web/onboarding/Checklist/LocationsModal/helpers/getMappedLocations';
import { getLocationsWithoutAddress } from '@web/onboarding/Checklist/LocationsModal/helpers/getLocationsWithoutAddress';
import { SCHEDULE_PATH_NAME } from '@web/onboarding/Checklist/onboardingChecklist.consts';

const useModalRefs = () => {
  const setScheduleModalRef = useRef(null);
  const addEmployeesModalRef = useRef(null);
  const timeclockModalRef = useRef(null);
  const locationsModalRef = useRef(null);
  const defineWagesModalRef = useRef(null);

  return {
    setScheduleModalRef,
    addEmployeesModalRef,
    timeclockModalRef,
    locationsModalRef,
    defineWagesModalRef,
  };
};

type Props = {
  clientFlags: ClientFlags;
  openCreateShiftModal: OpenCreateShiftModal;
  navigateToReports: NavigateToReports;
  navigateToSchedule: NavigateToSchedule;
  locations: LocationType[];
  personasDict: PersonasDict;
  employees: RestangularEmployee[];
  positions: RestangularPosition[];
  isLoaded: boolean;
  showPublishShiftModal: ShowPublishShiftModal;
  showPublishShiftTooltip: ShowPublishShiftTooltip;
  allEvents: Event[];
  orgSettings: OrgSettings;
  autoOpenOnboarding: boolean;
};

export const useOnboardingChecklist = ({
  clientFlags,
  openCreateShiftModal,
  navigateToReports,
  navigateToSchedule,
  locations,
  personasDict,
  isLoaded,
  showPublishShiftModal,
  showPublishShiftTooltip,
  allEvents,
  orgSettings,
  autoOpenOnboarding,
  employees,
  positions,
}: Props) => {
  const {
    setScheduleModalRef,
    addEmployeesModalRef,
    timeclockModalRef,
    locationsModalRef,
    defineWagesModalRef,
  } = useModalRefs();
  const [isOpen, setIsOpen] = useState(true);
  const completeStep = useCompleteStep();
  const { availableSteps } = useStepsConfig();
  const locationsWithoutAddress = getLocationsWithoutAddress(getMappedLocations(locations));
  const isTimeTrackingEnabled = useMemo(
    () => orgSettings.clockInFromWebAndApps && orgSettings.clockInEnabled,
    [orgSettings]
  );

  useAutocompleteSteps({
    availableSteps,
    completeStep,
    clientFlags,
    personasDict,
    allEvents,
    isTimeTrackingEnabled,
    employees,
    positions,
  });

  const { publishSchedule, publishScheduleModalRef } = usePublishSchedule({
    isLoaded,
    showPublishShiftModal,
    showPublishShiftTooltip,
    allEvents,
    navigateToSchedule,
  });

  useEffect(() => {
    if (!isOpen && autoOpenOnboarding) {
      setIsOpen(true);
    }
  }, [autoOpenOnboarding, isOpen]);

  const goToSchedule = (): void => {
    if (window.location.pathname === SCHEDULE_PATH_NAME) {
      openCreateShiftModal();
      return;
    }

    navigateToSchedule({ date: formatDate(getNowDate()) });
  };

  const setSchedule = async (): Promise<void> => {
    setScheduleModalRef.current?.open();
    await completeStep(OnboardingTrialStepsType.setScheduleRules, availableSteps);
  };

  const openTimeclockModal = (): void => {
    timeclockModalRef.current?.open();
  };

  const openDefineWagesModal = (): void => {
    defineWagesModalRef.current?.open();
  };

  const stepsConfig: OnboardingStepsConfigType = availableSteps.reduce((acc, stepKey) => {
    switch (stepKey) {
      case OnboardingTrialStepsType.addEmployees: {
        acc[stepKey] = {
          content: gettext('Add employees'),
          action: addEmployeesModalRef.current?.open,
        };
        return acc;
      }

      case OnboardingTrialStepsType.createShifts: {
        acc[stepKey] = {
          content: gettext('Create shifts'),
          action: goToSchedule,
        };
        return acc;
      }

      case OnboardingTrialStepsType.defineWages: {
        acc[stepKey] = {
          content: gettext('Define wages'),
          action: openDefineWagesModal,
        };
        return acc;
      }

      case OnboardingTrialStepsType.viewReports: {
        acc[stepKey] = {
          content: gettext('View reports'),
          action: navigateToReports,
        };
        return acc;
      }

      case OnboardingTrialStepsType.setScheduleRules: {
        acc[stepKey] = {
          content: gettext('Set schedule rules'),
          action: setSchedule,
        };
        return acc;
      }

      case OnboardingTrialStepsType.enableTimeClock: {
        acc[stepKey] = {
          content: gettext('Enable time clock'),
          action: openTimeclockModal,
        };
        return acc;
      }

      case OnboardingTrialStepsType.publishSchedule: {
        acc[stepKey] = {
          content: gettext('Publish schedule'),
          action: publishSchedule,
        };
        return acc;
      }
    }
  }, {});

  const timeclockSaveCallback = () => {
    setTimeout(() => {
      locationsModalRef.current?.open();
    }, 1000);
  };

  return {
    supportedSteps: getSupportedSteps(clientFlags, availableSteps),
    stepsConfig,
    isOpen,
    setIsOpen,
    setScheduleModalRef,
    timeclockModalRef,
    addEmployeesModalRef,
    locationsWithoutAddress,
    locationsModalRef,
    timeclockSaveCallback,
    publishScheduleModalRef,
    defineWagesModalRef,
    openDefineWagesModal,
  };
};
